import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { VegaEmbed } from 'react-vega';
import { ClickableTile } from '@carbon/react';
import { useApiWidgetData } from 'src/lib/hooks/api';
import _cloneDeep from 'lodash/cloneDeep';
import { useFeatureFlag } from 'src/lib/hooks';


const transformSpec = (spec, data) => { // This function takes in a spec object and data object and returns a spec object with the data injected.
  const finalSpec = (spec && _cloneDeep(spec)) || {}; // deep clone the spec so we don't mutate the original.
  finalSpec.data = [].concat(finalSpec.data || {}); // ensure data is an array.

  if (finalSpec?.data?.[0]) { // if there is a data object in the spec, inject the data.
    finalSpec.data[0].values = data;
  }

  // add default width, height if not available in spec
  finalSpec.width = finalSpec.width ? finalSpec.width : 220;
  finalSpec.height = finalSpec.height ? finalSpec.height : 150;
  finalSpec.padding = finalSpec.padding ? finalSpec.padding : 0;
  finalSpec.autosize = { type: 'fit', contains: 'padding' };

  return finalSpec;
};

export const KDWidget = (props) => {
  const [ connectionTimedOut, setConnectionTimedOut ] = useState(false);
  const router = useRouter();
  const [ vegaActionSignal, setVegaActionSignal ] = useState(null);

  const featureAllowWidgetControlClick = useFeatureFlag('allow_widget_to_control_click_functionality');

  // Signal listeners for vega actions.
  const signalListeners = {
    actionClick: signal => featureAllowWidgetControlClick && setVegaActionSignal(signal),
  };

  const spec = props.widget.visualization.spec;
  const key = `${ props.widget.account }-${ props.widget.name }`;

  // Fetch the widget data
  const { data: widgetData, error: widgetError, isLoading: widgetLoading, isIdle: widgetIdle } = useApiWidgetData({
    key,
    vegaData: spec?.data?.[0],
    esInstance: props.widget.esInstance,
    dashboardId: props.widget.dashboardId,
    shouldWait: props.isLoading,
    filterDataByOU: props.widget.filterDataByOU,
  });

  // combine the data with the spec
  const finalSpec = transformSpec(spec, widgetData);

  const isDashboardDisabled = props.widget.isDashboardDisabled;
  const combinedIsLoading = props.isLoading || widgetLoading || widgetIdle;

  // Using a timeout so that we delay showing an error in the case where multiple kibana instances need to be logged into.
  useEffect(() => {
    let timeout = undefined;
    timeout = setTimeout(() => {
      setConnectionTimedOut(true);
    }, 11000); // 11 seconds

    return () => {
      clearTimeout(timeout);
    };
  }, [ props.isLoading, widgetLoading ]);

  // hard-code widget size based on spec so it doesn't jump after loading
  const sizeStyles = {
    width: finalSpec?.width + 32,
    height: finalSpec?.height + 32,
  };

  return (
    <div className="widget">
      <ClickableTile
        href={ (combinedIsLoading || isDashboardDisabled || vegaActionSignal) ? null : `/dashboard/${ encodeURIComponent(props.widget.dashboardId) }` }
        target="_blank"
        rel="noopener"
        className={ `widget__wrapper ${ widgetLoading ? 'loading' : null }` }
        style={ sizeStyles }
        onClick={ (e) => {
          e.preventDefault();
          // load with client-side router, much faster
          if (combinedIsLoading || isDashboardDisabled || vegaActionSignal) { return; }
          router.push(`/dashboard/${ encodeURIComponent(props.widget.dashboardId) }`);
        } }
      >
        {(combinedIsLoading || (isDashboardDisabled && !connectionTimedOut)) ? (
          <span>Loading...</span>
        ) : (
          <>
            {isDashboardDisabled && (
              <div className="error-text">This widget is temporarily unavailable.</div>
            )}

            {!isDashboardDisabled && widgetError && (
              <div className="error-text">Failed to load widget data.</div>
            )}

            {!isDashboardDisabled && widgetData && <VegaEmbed spec={ finalSpec } actions={ false } signalListeners={ signalListeners } />}
          </>
        )}
      </ClickableTile>
    </div>
  );
};
